import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useCharacterStore } from "~/stores/characterStore";
import type { Relationship } from "~/types/storage";

/**
 * Hook for managing displayed character (with URL param support)
 *
 * Handles the logic of "which character am I viewing" including URL parameter
 * support for viewing specific characters without changing the global active character.
 *
 * @returns Character data and update functions for the displayed character
 */
export function useDisplayedCharacter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const characters = useCharacterStore((state) => state.characters);
  const updateCharacter = useCharacterStore((state) => state.updateCharacter);
  const getActiveCharacter = useCharacterStore((state) => state.getActiveCharacter);

  const activeCharacterData = getActiveCharacter();
  const activeCharacterId = activeCharacterData?.id || '';

  // Local viewing ID - can be overridden by URL param without affecting global activeCharacterId
  const [viewingCharacterId, setViewingCharacterId] = useState<string | null>(null);

  // Handle URL query parameter for character ID (local only, doesn't persist)
  useEffect(() => {
    const urlCharacterId = searchParams.get("id");
    if (urlCharacterId) {
      const exists = characters.some((c) => c.id === urlCharacterId);
      if (exists) {
        setViewingCharacterId(urlCharacterId);
      } else {
        // Invalid ID - clear the param
        setSearchParams({}, { replace: true });
        setViewingCharacterId(null);
      }
    } else {
      setViewingCharacterId(null);
    }
  }, [searchParams, characters, setSearchParams]);

  // Determine which character to display
  const displayedCharacterId = viewingCharacterId || activeCharacterId;
  const characterData = characters.find((c) => c.id === displayedCharacterId) || activeCharacterData;

  // Update functions that work on the displayed character
  const updateName = useCallback(
    (newName: string) => {
      updateCharacter(displayedCharacterId, { name: newName });
    },
    [displayedCharacterId, updateCharacter]
  );

  const updateCoreStats = useCallback(
    (newCoreStats: Record<string, number>) => {
      updateCharacter(displayedCharacterId, { coreStats: newCoreStats });
    },
    [displayedCharacterId, updateCharacter]
  );

  const updateCurrentHp = useCallback(
    (newHp: number) => {
      updateCharacter(displayedCharacterId, { currentHp: newHp });
    },
    [displayedCharacterId, updateCharacter]
  );

  const updateCurrentResilience = useCallback(
    (newResilience: number) => {
      updateCharacter(displayedCharacterId, { currentResilience: newResilience });
    },
    [displayedCharacterId, updateCharacter]
  );

  const updateStrain = useCallback(
    (newStrain: number) => {
      updateCharacter(displayedCharacterId, { strain: newStrain });
    },
    [displayedCharacterId, updateCharacter]
  );

  const updateItems = useCallback(
    (newItems: { id: string; content: string }[]) => {
      updateCharacter(displayedCharacterId, { items: newItems });
    },
    [displayedCharacterId, updateCharacter]
  );

  const updateAppearance = useCallback(
    (newAppearance: string) => {
      updateCharacter(displayedCharacterId, { appearance: newAppearance });
    },
    [displayedCharacterId, updateCharacter]
  );

  const updateRelationships = useCallback(
    (newRelationships: Relationship[]) => {
      updateCharacter(displayedCharacterId, { relationships: newRelationships });
    },
    [displayedCharacterId, updateCharacter]
  );

  const updateNotes = useCallback(
    (newNotes: string) => {
      updateCharacter(displayedCharacterId, { notes: newNotes });
    },
    [displayedCharacterId, updateCharacter]
  );

  // Export the displayed character
  const exportCharacter = useCallback(() => {
    const filename = characterData.name.replace(/\s+/g, "_") || "character";
    const blob = new Blob([JSON.stringify(characterData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [characterData]);

  // Clear URL param helper (for character search)
  const clearViewingOverride = useCallback(() => {
    if (viewingCharacterId) {
      setSearchParams({}, { replace: true });
      setViewingCharacterId(null);
    }
  }, [viewingCharacterId, setSearchParams]);

  return {
    characterData,
    displayedCharacterId,
    viewingCharacterId,
    updateName,
    updateCoreStats,
    updateCurrentHp,
    updateCurrentResilience,
    updateStrain,
    updateItems,
    updateAppearance,
    updateRelationships,
    updateNotes,
    exportCharacter,
    clearViewingOverride,
  };
}
